import { useState, type ReactNode } from "react";
import { Icon } from "./Icon";

/* ── PageGuide ──────────────────────────────────────────────────────────
 * A collapsible "how to use this page" panel that sits under a PageHeader.
 * It explains what a screen is for and how to drive it — aimed at staff who
 * land on an unfamiliar page (the quote-template editor being the prime
 * example). Open by default; once a user collapses it the choice is
 * remembered per `id` in localStorage, so it stays out of the way after the
 * page is understood but is always one click from coming back.
 *
 *   <PageGuide id="quote-templates" title="How quote templates work"
 *              summary="A reusable offer + the knobs a prospect may adjust.">
 *     <GuideSteps items={[...]} />
 *     <GuideTerms items={[{ term: "Slug", def: "…" }]} />
 *   </PageGuide>
 */

const STORAGE_PREFIX = "fd.guide.";

function readOpen(id: string): boolean | null {
  try {
    const v = window.localStorage.getItem(STORAGE_PREFIX + id);
    return v === null ? null : v === "1";
  } catch {
    return null;
  }
}

function writeOpen(id: string, open: boolean) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + id, open ? "1" : "0");
  } catch {
    /* private mode / storage disabled — guide just won't persist */
  }
}

export interface PageGuideProps {
  /** Stable key used to remember the collapsed/expanded choice. */
  id: string;
  title: ReactNode;
  /** One line shown next to the title — the gist when collapsed. */
  summary?: ReactNode;
  children: ReactNode;
  /** Whether the guide starts expanded the first time it's seen. */
  defaultOpen?: boolean;
}

export function PageGuide({ id, title, summary, children, defaultOpen = true }: PageGuideProps) {
  const [open, setOpen] = useState<boolean>(() => {
    const stored = readOpen(id);
    return stored === null ? defaultOpen : stored;
  });
  const bodyId = `fd-guide-${id}`;

  function toggle() {
    setOpen((prev) => {
      const next = !prev;
      writeOpen(id, next);
      return next;
    });
  }

  return (
    <section className={["fd-guide", open && "fd-guide--open"].filter(Boolean).join(" ")}>
      <button
        type="button"
        className="fd-guide__head"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={toggle}
      >
        <span className="fd-guide__badge">
          <Icon name="help" size={16} />
        </span>
        <span className="fd-guide__heading">
          <span className="fd-guide__title">{title}</span>
          {summary && <span className="fd-guide__summary">{summary}</span>}
        </span>
        <span className="fd-guide__toggle">
          {open ? "Hide" : "Show"}
          <Icon name="chevronDown" size={16} className="fd-guide__chevron" />
        </span>
      </button>
      {open && (
        <div className="fd-guide__body" id={bodyId}>
          {children}
        </div>
      )}
    </section>
  );
}

/* ── Content helpers ────────────────────────────────────────────────────
 * Small building blocks so every guide reads consistently: a titled block,
 * a numbered walkthrough, a field glossary, and a code/JSON sample.
 */

export function GuideSection({ title, children }: { title?: ReactNode; children: ReactNode }) {
  return (
    <div className="fd-guide__section">
      {title && <div className="fd-guide__section-title">{title}</div>}
      <div className="fd-guide__section-body">{children}</div>
    </div>
  );
}

export function GuideSteps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="fd-guide__steps">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ol>
  );
}

export interface GuideTerm {
  term: ReactNode;
  def: ReactNode;
}

export function GuideTerms({ items }: { items: GuideTerm[] }) {
  return (
    <dl className="fd-guide__terms">
      {items.map((it, i) => (
        <div className="fd-guide__term" key={i}>
          <dt>{it.term}</dt>
          <dd>{it.def}</dd>
        </div>
      ))}
    </dl>
  );
}

export function GuideCode({ children, label }: { children: ReactNode; label?: ReactNode }) {
  return (
    <figure className="fd-guide__code">
      {label && <figcaption className="fd-guide__code-label">{label}</figcaption>}
      <pre>
        <code>{children}</code>
      </pre>
    </figure>
  );
}
